import type { Drama } from '@/mocks/dramas';

const barColor = (score: number) => {
  if (score >= 80) return 'bg-secondary-500';
  if (score >= 62) return 'bg-accent-500';
  return 'bg-primary-500';
};

export default function AnalysisSection({ drama }: { drama: Drama }) {
  return (
    <>
      <section id="score" className="scroll-mt-28 pt-2">
        <div className="flex items-center gap-3 mb-5">
          <span className="font-label text-xs text-foreground-400">03</span>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            改编程度分析
          </h2>
        </div>

        <p className="text-sm text-foreground-600 leading-relaxed mb-5">
          我们把「像不像」拆成五个可以分开评估的维度。每一维度独立打分（0—100），分数越高表示越贴近真实，并附上打分依据。
        </p>

        <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
          <ul className="space-y-5">
            {drama.dimensions.map((dim) => (
              <li key={dim.label}>
                <div className="flex items-center justify-between gap-4 mb-2">
                  <span className="text-sm font-medium text-foreground-900">{dim.label}</span>
                  <span className="font-label text-sm text-foreground-700">{dim.score}</span>
                </div>
                <div className="h-2 w-full rounded-full bg-background-300 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${barColor(dim.score)}`}
                    style={{ width: `${dim.score}%` }}
                  ></div>
                </div>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{dim.note}</p>
              </li>
            ))}
          </ul>

          <div className="mt-6 pt-5 border-t border-background-200 flex flex-wrap items-center gap-4">
            <span className="text-xs text-foreground-500">综合还原度</span>
            <span className="font-heading text-2xl font-black text-primary-600">
              {drama.adaptationScore}
              <span className="text-sm font-medium text-foreground-500 ml-0.5">%</span>
            </span>
            <span className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-secondary-500"></span>
                80 以上 · 高度贴近
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-accent-500"></span>
                62—79 · 有加工
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-primary-500"></span>
                62 以下 · 虚构明显
              </span>
            </span>
          </div>
        </div>
      </section>

      <section id="compare" className="scroll-mt-28 pt-2">
        <div className="flex items-center gap-3 mb-5">
          <span className="font-label text-xs text-foreground-400">04</span>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            与真实爆料对比
          </h2>
        </div>

        <p className="text-sm text-foreground-600 leading-relaxed mb-5">
          同一件事，真实情况是怎样的、剧里改成了什么样，逐条列出来。左边是对照维度，中间是公开信源中的实际情况，右边是剧中的呈现方式。
        </p>

        <div className="space-y-4">
          {drama.comparisons.map((row) => (
            <div
              key={row.aspect}
              className="rounded-lg border border-background-200 overflow-hidden"
            >
              <div className="px-5 py-3 bg-foreground-950 flex items-center gap-2">
                <i className="ri-bookmark-3-line text-accent-400"></i>
                <h3 className="font-heading text-sm font-bold text-background-50">
                  {row.aspect}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="p-5 bg-secondary-50 border-b md:border-b-0 md:border-r border-background-200">
                  <p className="flex items-center gap-2 text-xs font-medium text-secondary-800 mb-2">
                    <i className="ri-file-text-line"></i>
                    真实爆料中的情况
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">{row.reality}</p>
                </div>
                <div className="p-5 bg-primary-50">
                  <p className="flex items-center gap-2 text-xs font-medium text-primary-700 mb-2">
                    <i className="ri-film-line"></i>
                    剧中的呈现
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">{row.drama}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}